import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
  useListCrewDeliveryTemplatesQuery,
  useCreateCrewDeliveryTemplateMutation,
  useUpdateCrewDeliveryTemplateMutation,
  useDeleteCrewDeliveryTemplateMutation,
} from '../../../redux/api/crewDeliveryApi';
import { useApiErrorToast } from '../../../hooks/useApiError';
import TemplateBuilder from './TemplateBuilder';

/**
 * Build a set of { article, quantity } lines once and stamp it onto every
 * location of the delivery. Templates are event-scoped and only ever fill the
 * normal delivery form — all contingent/overbooking guards apply unchanged
 * afterwards.
 *
 * Apply uses Replace semantics (overwrites each location's articles) and asks
 * for confirmation first. Loading a saved template resolves its articles
 * against the current event's crew stock; articles not in stock are skipped
 * with a notice (manual entry can only pick stocked articles either).
 *
 * `onApplyToAll(items)` receives [{ stock, quantity }] using the live stock
 * objects, so the parent can push them straight into its delivery lines.
 */
const TemplatePanel = ({
  eventId,
  stocks = [],
  locationCount = 0,
  onApplyToAll,
}) => {
  const authState = useSelector((s) => s.auth);
  const apiErrorToast = useApiErrorToast();

  const crewStocks = useMemo(() => stocks.filter((s) => s.crew), [stocks]);

  const [open, setOpen] = useState(false); // collapsed on load for a clean UI
  const [draft, setDraft] = useState([]); // [{ stock, quantity }]
  const [loadedTemplate, setLoadedTemplate] = useState(null); // { id, name, created_by }
  const [selectedId, setSelectedId] = useState('');
  const [skipped, setSkipped] = useState([]); // names skipped on load
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [nameInput, setNameInput] = useState('');

  const { data: templates } = useListCrewDeliveryTemplatesQuery(
    { eventId },
    { skip: !eventId }
  );
  const [createTemplate, { isLoading: creating }] =
    useCreateCrewDeliveryTemplateMutation();
  const [updateTemplate, { isLoading: updating }] =
    useUpdateCrewDeliveryTemplateMutation();
  const [deleteTemplate] = useDeleteCrewDeliveryTemplateMutation();

  const canManage =
    !!loadedTemplate &&
    (authState.isSuperUser || loadedTemplate.created_by === authState.id);

  // --- draft editing ---
  const addArticle = (stock) =>
    setDraft((prev) =>
      prev.some((d) => d.stock.id === stock.id)
        ? prev
        : [...prev, { stock, quantity: 1 }]
    );
  const removeArticle = (stockId) =>
    setDraft((prev) => prev.filter((d) => d.stock.id !== stockId));
  const changeQuantity = (stockId, quantity) =>
    setDraft((prev) =>
      prev.map((d) => (d.stock.id === stockId ? { ...d, quantity } : d))
    );
  const resetDraft = () => {
    setDraft([]);
    setLoadedTemplate(null);
    setSelectedId('');
    setSkipped([]);
  };

  // --- load a saved template into the draft ---
  const loadTemplate = (templateId) => {
    setSelectedId(templateId);
    if (!templateId) {
      resetDraft();
      return;
    }
    const tpl = (templates || []).find((t) => t.id === templateId);
    if (!tpl) return;

    const nextDraft = [];
    const skippedNames = [];
    tpl.items.forEach((item) => {
      const stock = crewStocks.find((s) => s.article === item.article);
      if (stock) nextDraft.push({ stock, quantity: item.quantity });
      else skippedNames.push(item.article_name);
    });

    setDraft(nextDraft);
    setSkipped(skippedNames);
    setLoadedTemplate({
      id: tpl.id,
      name: tpl.name,
      created_by: tpl.created_by,
    });
    if (skippedNames.length > 0) {
      toast.warning(
        `Nicht im Bestand, übersprungen: ${skippedNames.join(', ')}`
      );
    }
  };

  // --- apply (Replace, with confirmation) ---
  const requestApply = () => {
    if (locationCount === 0) return;
    if (draft.length === 0) {
      toast.error('Vorlage ist leer.');
      return;
    }
    setShowConfirm(true);
  };
  const confirmApply = () => {
    onApplyToAll(draft.map((d) => ({ stock: d.stock, quantity: d.quantity })));
    setShowConfirm(false);
    toast.success(`Vorlage auf ${locationCount} Lieferstelle(n) angewendet.`);
  };

  // --- save / update / delete ---
  const buildItemsPayload = () =>
    draft.map((d, idx) => ({
      article_id: d.stock.article,
      quantity: d.quantity,
      order_key: idx,
    }));

  const openSave = () => {
    setNameInput('');
    setShowSave(true);
  };
  const submitSave = async () => {
    const name = nameInput.trim();
    if (!name) {
      toast.error('Bitte einen Namen angeben.');
      return;
    }
    if (draft.length === 0) {
      toast.error('Vorlage ist leer.');
      return;
    }
    try {
      const result = await createTemplate({
        eventId,
        name,
        items: buildItemsPayload(),
      }).unwrap();
      toast.success('Vorlage gespeichert.');
      setShowSave(false);
      setSelectedId(result.id);
      setLoadedTemplate({
        id: result.id,
        name: result.name,
        created_by: result.created_by,
      });
    } catch (err) {
      apiErrorToast(err, 'Vorlage konnte nicht gespeichert werden.');
    }
  };
  const submitUpdate = async () => {
    if (!loadedTemplate || draft.length === 0) return;
    try {
      await updateTemplate({
        id: loadedTemplate.id,
        name: loadedTemplate.name,
        items: buildItemsPayload(),
      }).unwrap();
      toast.success('Vorlage aktualisiert.');
    } catch (err) {
      apiErrorToast(err, 'Vorlage konnte nicht aktualisiert werden.');
    }
  };
  const submitDelete = async () => {
    if (!loadedTemplate) return;
    try {
      await deleteTemplate({ id: loadedTemplate.id }).unwrap();
      toast.success('Vorlage gelöscht.');
      resetDraft();
    } catch (err) {
      apiErrorToast(err, 'Vorlage konnte nicht gelöscht werden.');
    }
  };

  return (
    <div className='card mb-4 border-primary-subtle'>
      <button
        type='button'
        className='card-header d-flex gap-2 align-items-center w-100 border-0 bg-transparent text-start'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronRight}
          className='text-muted'
          fixedWidth
        />
        <span className='fw-bold'>Vorlage</span>
        {draft.length > 0 && (
          <span className='badge bg-secondary'>{draft.length}</span>
        )}
        {!open && (
          <span className='text-muted small ms-1'>
            Artikel-Vorlage auf alle Lieferstellen anwenden
          </span>
        )}
      </button>

      {open && (
        <>
          <div className='card-body'>
            {templates && templates.length > 0 && (
              <select
                className='form-select form-select-sm mb-3'
                style={{ maxWidth: '18rem' }}
                value={selectedId}
                onChange={(e) => loadTemplate(e.target.value)}
              >
                <option value=''>Gespeicherte Vorlage laden…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
            <TemplateBuilder
              draft={draft}
              crewStocks={crewStocks}
              onAddArticle={addArticle}
              onRemoveArticle={removeArticle}
              onChangeQuantity={changeQuantity}
            />
            {skipped.length > 0 && (
              <div className='alert alert-warning py-2 small mt-2 mb-0'>
                Nicht im Bestand und übersprungen: {skipped.join(', ')}
              </div>
            )}
          </div>

          <div className='card-footer d-flex flex-wrap gap-2 align-items-center'>
            <Button
              size='sm'
              variant='primary'
              disabled={locationCount === 0 || draft.length === 0}
              onClick={requestApply}
            >
              Auf alle Lieferstellen anwenden
              {locationCount > 0 ? ` (${locationCount})` : ''}
            </Button>
            <Button
              size='sm'
              variant='outline-secondary'
              disabled={draft.length === 0}
              onClick={openSave}
            >
              Als Vorlage speichern
            </Button>
            {loadedTemplate && canManage && (
              <>
                <Button
                  size='sm'
                  variant='outline-secondary'
                  disabled={draft.length === 0 || updating}
                  onClick={submitUpdate}
                >
                  „{loadedTemplate.name}" aktualisieren
                </Button>
                <Button
                  size='sm'
                  variant='outline-danger'
                  onClick={submitDelete}
                >
                  Löschen
                </Button>
              </>
            )}
            {draft.length > 0 && (
              <Button
                size='sm'
                variant='link'
                className='text-secondary'
                onClick={resetDraft}
              >
                Zurücksetzen
              </Button>
            )}
            {locationCount === 0 && (
              <span className='text-muted small ms-auto'>
                Erst Lieferstellen hinzufügen, dann anwenden.
              </span>
            )}
          </div>
        </>
      )}

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Vorlage anwenden</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bestehende Artikel auf{' '}
          <strong>allen {locationCount} Lieferstellen</strong> werden durch die
          Vorlage ersetzt. Fortfahren?
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirm(false)}>
            Abbrechen
          </Button>
          <Button variant='primary' onClick={confirmApply}>
            Ersetzen
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSave} onHide={() => setShowSave(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Als Vorlage speichern</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className='form-label small'>Name der Vorlage</label>
          <input
            className='form-control'
            value={nameInput}
            autoFocus
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitSave();
            }}
          />
          <div className='text-muted small mt-2'>
            {draft.length} Artikel werden gespeichert.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowSave(false)}>
            Abbrechen
          </Button>
          <Button variant='primary' disabled={creating} onClick={submitSave}>
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TemplatePanel;
